/**
 * Fetches nearby public-transit stop nodes from OpenStreetMap via the Overpass API.
 * Used for bulk postcode → nearest stop assignment without a separate stops spreadsheet.
 */

const DEFAULT_OVERPASS = 'https://overpass-api.de/api/interpreter';

/** Public Overpass endpoints are heavily rate-limited; keep batches small and retries polite. */
const OVERPASS_RETRYABLE_STATUS = new Set([429, 502, 503, 504]);
const OVERPASS_MAX_ATTEMPTS = 8;
const OVERPASS_BACKOFF_CAP_MS = 90_000;
const OVERPASS_BACKOFF_BASE_MS = 4000;

type OverpassElement = {
  type: string;
  id: number;
  lat?: number;
  lon?: number;
  tags?: Record<string, string>;
};

export type OsmTransitStopCandidate = {
  osmId: string;
  name: string;
  lat: number;
  lon: number;
  kind: string;
};

/** Clamps Overpass search radius to a safe range for public instances (metres). */
export function clampOsmAssignmentSearchRadius(radiusMeters: number): number {
  if (!Number.isFinite(radiusMeters)) return 1000;
  return Math.min(5000, Math.max(80, Math.round(radiusMeters)));
}

export function getOsmStopCacheKey(lat: number, lon: number, radiusMeters: number): string {
  const r = clampOsmAssignmentSearchRadius(radiusMeters);
  return `${lat.toFixed(4)}_${lon.toFixed(4)}_${r}`;
}

function inferTransitKind(tags: Record<string, string>): string {
  if (tags.highway === 'bus_stop') return 'bus_stop';
  if (tags.railway === 'tram_stop') return 'tram_stop';
  if (tags.railway === 'halt') return 'rail_halt';
  if (tags.public_transport === 'stop_position') return 'stop_position';
  return 'transit';
}

function formatStopName(tags: Record<string, string>, id: number): string {
  const raw =
    tags.name?.trim() ||
    tags['naptan:CommonName']?.trim() ||
    tags.ref?.trim() ||
    tags.local_ref?.trim() ||
    '';
  if (raw) return raw;
  return `Transit stop ${id}`;
}

function haversineDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusM = 6_371_000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusM * c;
}

/**
 * Filters parsed OSM candidates to those within crow‑fly radius of one centre (metres).
 * Exported for unit tests.
 */
export function filterTransitStopsForCenter(
  centerLat: number,
  centerLon: number,
  radiusMeters: number,
  candidates: OsmTransitStopCandidate[]
): OsmTransitStopCandidate[] {
  const r = clampOsmAssignmentSearchRadius(radiusMeters);
  const limit = r + 0.5;
  return candidates.filter((stop) => haversineDistanceMeters(centerLat, centerLon, stop.lat, stop.lon) <= limit);
}

function buildAroundQueryForCenters(centers: Array<{ lat: number; lon: number }>, radiusMeters: number): string {
  const r = clampOsmAssignmentSearchRadius(radiusMeters);
  if (!centers.length) {
    throw new Error('At least one centre is required for an Overpass around query');
  }
  const blocks: string[] = [];
  for (const c of centers) {
    blocks.push(`  node["highway"="bus_stop"](around:${r},${c.lat},${c.lon});`);
    blocks.push(`  node["public_transport"="stop_position"](around:${r},${c.lat},${c.lon});`);
    blocks.push(`  node["railway"="tram_stop"](around:${r},${c.lat},${c.lon});`);
    blocks.push(`  node["railway"="halt"](around:${r},${c.lat},${c.lon});`);
  }
  const timeout = Math.min(120, 20 + centers.length * 7);
  return `
[out:json][timeout:${timeout}];
(
${blocks.join('\n')}
);
out body;
`.trim();
}

type BoundingBox = { south: number; west: number; north: number; east: number };

function metersToLatitudeDegrees(meters: number): number {
  return meters / 111_320;
}

function metersToLongitudeDegrees(meters: number, atLatitude: number): number {
  const cosLat = Math.cos((atLatitude * Math.PI) / 180);
  const denom = 111_320 * Math.max(0.01, cosLat);
  return meters / denom;
}

function buildBoundingBoxForCenters(centers: Array<{ lat: number; lon: number }>, radiusMeters: number): BoundingBox {
  let minLat = Number.POSITIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  let minLon = Number.POSITIVE_INFINITY;
  let maxLon = Number.NEGATIVE_INFINITY;
  for (const c of centers) {
    if (c.lat < minLat) minLat = c.lat;
    if (c.lat > maxLat) maxLat = c.lat;
    if (c.lon < minLon) minLon = c.lon;
    if (c.lon > maxLon) maxLon = c.lon;
  }
  const midLat = (minLat + maxLat) / 2;
  const padLat = metersToLatitudeDegrees(radiusMeters);
  const padLon = metersToLongitudeDegrees(radiusMeters, midLat);
  return {
    south: minLat - padLat,
    west: minLon - padLon,
    north: maxLat + padLat,
    east: maxLon + padLon,
  };
}

function splitBoundingBoxIntoTiles(bbox: BoundingBox): BoundingBox[] {
  const latMid = (bbox.south + bbox.north) / 2;
  const lonMid = (bbox.west + bbox.east) / 2;
  return [
    { south: bbox.south, west: bbox.west, north: latMid, east: lonMid },
    { south: bbox.south, west: lonMid, north: latMid, east: bbox.east },
    { south: latMid, west: bbox.west, north: bbox.north, east: lonMid },
    { south: latMid, west: lonMid, north: bbox.north, east: bbox.east },
  ];
}

function buildBoundingBoxQuery(bbox: BoundingBox, estimatedCenters: number): string {
  const timeout = Math.min(150, 35 + estimatedCenters * 2);
  const area =
    `(${bbox.south.toFixed(6)},${bbox.west.toFixed(6)},${bbox.north.toFixed(6)},${bbox.east.toFixed(6)})`;
  return `
[out:json][timeout:${timeout}];
(
  node["highway"="bus_stop"]${area};
  node["public_transport"="stop_position"]${area};
  node["railway"="tram_stop"]${area};
  node["railway"="halt"]${area};
);
out body;
`.trim();
}

/**
 * Parses an Overpass JSON body into deduplicated transit stop candidates (nodes only).
 */
export function parseOverpassTransitStopsPayload(data: { elements?: OverpassElement[] }): OsmTransitStopCandidate[] {
  const elements = data.elements ?? [];
  const byId = new Map<number, OsmTransitStopCandidate>();

  for (const el of elements) {
    if (el.type !== 'node' || typeof el.lat !== 'number' || typeof el.lon !== 'number') continue;
    const tags = el.tags ?? {};
    const isTransit =
      tags.highway === 'bus_stop' ||
      tags.public_transport === 'stop_position' ||
      tags.railway === 'tram_stop' ||
      tags.railway === 'halt';
    if (!isTransit) continue;

    byId.set(el.id, {
      osmId: String(el.id),
      name: formatStopName(tags, el.id),
      lat: el.lat,
      lon: el.lon,
      kind: inferTransitKind(tags),
    });
  }

  return Array.from(byId.values());
}

function parseRetryAfterMs(response: Response): number | null {
  const raw = response.headers.get('Retry-After');
  if (!raw) return null;
  const sec = Number.parseInt(raw.trim(), 10);
  if (!Number.isFinite(sec) || sec < 0) return null;
  return Math.min(OVERPASS_BACKOFF_CAP_MS, Math.max(1000, sec * 1000));
}

function backoffMsForRateLimit(attemptIndex: number, response: Response): number {
  const fromHeader = parseRetryAfterMs(response);
  if (fromHeader != null) return fromHeader;
  return Math.min(
    OVERPASS_BACKOFF_CAP_MS,
    OVERPASS_BACKOFF_BASE_MS * 2 ** attemptIndex
  );
}

async function postOverpass(
  query: string,
  options?: { signal?: AbortSignal }
): Promise<Response> {
  const body = `data=${encodeURIComponent(query)}`;
  return fetch(DEFAULT_OVERPASS, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      // Overpass usage policy: identify the application (helps fair use vs generic clients).
      'User-Agent': 'atlas/postcode-stop-assignment',
    },
    body,
    signal: options?.signal ?? AbortSignal.timeout(90_000),
  });
}

async function cancellableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  if (!signal) {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    });
    return;
  }
  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

async function fetchOverpassTransitStopsQuery(
  query: string,
  options?: { signal?: AbortSignal }
): Promise<OsmTransitStopCandidate[]> {
  const signal = options?.signal;
  let response: Response | null = null;

  for (let attempt = 0; attempt < OVERPASS_MAX_ATTEMPTS; attempt += 1) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    response = await postOverpass(query, options);

    if (response.ok) {
      const json = (await response.json()) as { elements?: OverpassElement[] };
      return parseOverpassTransitStopsPayload(json);
    }

    if (!OVERPASS_RETRYABLE_STATUS.has(response.status)) {
      break;
    }

    if (attempt === OVERPASS_MAX_ATTEMPTS - 1) {
      break;
    }

    const waitMs = backoffMsForRateLimit(attempt, response);
    await cancellableDelay(waitMs, signal);
  }

  const status = response?.status ?? 'unknown';
  const text = response?.statusText ?? 'no response';
  throw new Error(`Overpass API error (${status}): ${text}`);
}

/**
 * Returns OSM transit nodes within the given crow‑fly radius of a point.
 *
 * @param lat WGS‑84 latitude
 * @param lon WGS‑84 longitude
 * @param radiusMeters Search radius (clamped roughly 80–5000 m)
 * @param options Optional AbortSignal for cancellation
 */
export async function fetchNearbyOsmTransitStops(
  lat: number,
  lon: number,
  radiusMeters: number,
  options?: { signal?: AbortSignal }
): Promise<OsmTransitStopCandidate[]> {
  const query = buildAroundQueryForCenters([{ lat, lon }], radiusMeters);
  return fetchOverpassTransitStopsQuery(query, options);
}

/** Default number of map centres combined into one Overpass request for postcode assignment. */
export const DEFAULT_OVERPASS_BATCH_SIZE = 8;
const DEFAULT_BBOX_TILE_CENTER_THRESHOLD = 120;

/**
 * One Overpass round‑trip for several centres: union of all `around` clauses, then split
 * candidates per cache key by crow‑fly distance.
 */
export async function fetchNearbyOsmTransitStopsBatch(
  centers: Array<{ lat: number; lon: number; cacheKey: string }>,
  radiusMeters: number,
  options?: { signal?: AbortSignal }
): Promise<Map<string, OsmTransitStopCandidate[]>> {
  const result = new Map<string, OsmTransitStopCandidate[]>();
  if (!centers.length) return result;

  const uniqueByKey = new Map<string, { lat: number; lon: number }>();
  for (const c of centers) {
    if (!uniqueByKey.has(c.cacheKey)) {
      uniqueByKey.set(c.cacheKey, { lat: c.lat, lon: c.lon });
    }
  }
  const uniqueList = Array.from(uniqueByKey.entries()).map(([cacheKey, p]) => ({
    cacheKey,
    lat: p.lat,
    lon: p.lon,
  }));

  const query = buildAroundQueryForCenters(
    uniqueList.map((e) => ({ lat: e.lat, lon: e.lon })),
    radiusMeters
  );
  const allCandidates = await fetchOverpassTransitStopsQuery(query, options);

  for (const { cacheKey, lat, lon } of uniqueList) {
    result.set(cacheKey, filterTransitStopsForCenter(lat, lon, radiusMeters, allCandidates));
  }

  return result;
}

/**
 * BBox-first Overpass strategy for many centers:
 * 1) query all transit nodes in one expanded bbox (or 4 tiled bboxes for very large center sets),
 * 2) locally filter candidates per center by crow-fly radius.
 * Falls back to around-batch strategy on bbox errors.
 */
export async function fetchNearbyOsmTransitStopsByBoundingBox(
  centers: Array<{ lat: number; lon: number; cacheKey: string }>,
  radiusMeters: number,
  options?: { signal?: AbortSignal; tileCenterThreshold?: number }
): Promise<Map<string, OsmTransitStopCandidate[]>> {
  const result = new Map<string, OsmTransitStopCandidate[]>();
  if (!centers.length) return result;

  const uniqueByKey = new Map<string, { lat: number; lon: number }>();
  for (const c of centers) {
    if (!uniqueByKey.has(c.cacheKey)) {
      uniqueByKey.set(c.cacheKey, { lat: c.lat, lon: c.lon });
    }
  }
  const uniqueList = Array.from(uniqueByKey.entries()).map(([cacheKey, p]) => ({
    cacheKey,
    lat: p.lat,
    lon: p.lon,
  }));

  const bbox = buildBoundingBoxForCenters(uniqueList, radiusMeters);
  const tileThreshold = options?.tileCenterThreshold ?? DEFAULT_BBOX_TILE_CENTER_THRESHOLD;
  const boxes = uniqueList.length > tileThreshold ? splitBoundingBoxIntoTiles(bbox) : [bbox];

  try {
    const byId = new Map<string, OsmTransitStopCandidate>();
    for (let i = 0; i < boxes.length; i += 1) {
      const query = buildBoundingBoxQuery(boxes[i]!, uniqueList.length);
      const candidates = await fetchOverpassTransitStopsQuery(query, { signal: options?.signal });
      for (const c of candidates) {
        if (!byId.has(c.osmId)) byId.set(c.osmId, c);
      }
    }
    const allCandidates = Array.from(byId.values());
    for (const { cacheKey, lat, lon } of uniqueList) {
      result.set(cacheKey, filterTransitStopsForCenter(lat, lon, radiusMeters, allCandidates));
    }
    return result;
  } catch {
    return fetchNearbyOsmTransitStopsBatch(uniqueList, radiusMeters, { signal: options?.signal });
  }
}
